import { isValid, parseISO } from 'date-fns';
import { injectable } from 'inversify';

import { getMediaStatusFromEventState } from '../utils/liveEvent';
import { GET_REELS_BY_PLAYLIST_ID, GetReelsByPlaylistIdData, GetReelsByPlaylistIdVariables } from '../graphql/query/reels.queries';
import { getAuthApolloClient } from '../graphql/ApolloClient';
import { createURL } from '../utils/urlFormatting';
import { getDataOrThrow } from '../utils/api';
import { filterMediaOffers } from '../utils/entitlements';
import { useConfigStore as ConfigStore } from '../stores/ConfigStore';
import type { GetPlaylistParams, Playlist, PlaylistItem } from '../../types/playlist';
import type { AdSchedule } from '../../types/ad-schedule';
import type { EpisodeInSeries, EpisodesRes, EpisodesWithPagination, GetSeriesParams, Series } from '../../types/series';
import env from '../env';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';

// change the values below to change the property used to look up the alternate image
enum ImageProperty {
  CARD = 'card',
  BACKGROUND = 'background',
  CHANNEL_LOGO = 'channel_logo',
}

const PAGE_LIMIT = 20;

@injectable()
export default class ApiService {
  /**
   * We use playlistLabel prop to define the label used for all media items inside.
   * That way we can change the behavior of the same media items being in different playlists
   */
  private generateAlternateImageURL = ({
    item,
    label,
    playlistLabel,
    isFeatured = false,
  }: {
    item: PlaylistItem;
    label: string;
    playlistLabel?: string;
    isFeatured?: boolean;
  }) => {
    const pathname = `/v2/media/${item.mediaid}/images/${playlistLabel || label}.webp`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { poster_fallback: 1, fallback: playlistLabel ? label : null });

    return isFeatured ? item.images.find((image) => image.width === 1920)?.src || url : url;
  };

  private parseDate = (item: PlaylistItem, prop: string) => {
    const date = item[prop] as string | undefined;

    if (date && !isValid(new Date(date))) {
      console.error(`Invalid "${prop}" date provided for the "${item.title}" media item`);
      return undefined;
    }

    return date ? parseISO(date) : undefined;
  };

  /**
   * Transform incoming media items
   * - Parses productId into MediaOffer[] for all cleeng offers
   */
  private transformMediaItem = (item: PlaylistItem, playlist?: Playlist, isFeatured?: boolean) => {
    const config = ConfigStore.getState().config;
    const offerKeys = Object.keys(config?.integrations || {})[0];
    const playlistLabel = playlist?.imageLabel;
    const { packageKey } = useAppInfo();

    const transformedMediaItem = {
      ...item,
      cardImage: this.generateAlternateImageURL({ item, label: ImageProperty.CARD, playlistLabel, isFeatured }),
      channelLogoImage: this.generateAlternateImageURL({ item, label: ImageProperty.CHANNEL_LOGO, playlistLabel }),
      backgroundImage: this.generateAlternateImageURL({ item, label: ImageProperty.BACKGROUND }),
      mediaOffers:
        packageKey && item[packageKey as keyof PlaylistItem] ? filterMediaOffers(offerKeys, item[packageKey as keyof PlaylistItem] as string) : undefined,
      scheduledStart: this.parseDate(item, 'VCH.ScheduledStart'),
      scheduledEnd: this.parseDate(item, 'VCH.ScheduledEnd'),
    };

    // add the media status to the media item after the transformation because the live media status depends on the scheduledStart and scheduledEnd
    transformedMediaItem.mediaStatus = getMediaStatusFromEventState(transformedMediaItem);

    return transformedMediaItem;
  };

  /**
   * Transform incoming playlists
   */
  private transformPlaylist = (playlist: Playlist, relatedMediaId?: string, isFeatured?: boolean) => {
    playlist.playlist = playlist.playlist.map((item) => this.transformMediaItem(item, playlist, isFeatured));

    // remove the related media item (when this is a recommendations playlist)
    if (relatedMediaId) {
      playlist.playlist = playlist.playlist.filter((item) => item.mediaid !== relatedMediaId);
    }
    return playlist;
  };

  private transformEpisodes = (episodesRes: EpisodesRes, seasonNumber?: number) => {
    const { episodes, page, page_limit, total } = episodesRes;

    // Adding images and keys for media items
    return {
      episodes: episodes
        .filter((el) => el.media_item)
        .map((el) => ({
          ...this.transformMediaItem(el.media_item as PlaylistItem),
          seasonNumber: seasonNumber?.toString() || el.season_number?.toString() || '',
          episodeNumber: String(el.episode_number),
        })),
      pagination: { page, page_limit, total },
    };
  };

  /**
   * Get playlist by id
   */
  getPlaylistById = async (id?: string, params: GetPlaylistParams = {}, isFeatured?: boolean): Promise<Playlist | undefined> => {
    if (!id) {
      return undefined;
    }

    const pathname = `/v2/playlists/${id}`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, params);
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    return this.transformPlaylist(data, params.related_media_id, isFeatured);
  };

  /**
   * Get watchlist by playlistId
   */
  getMediaByWatchlist = async (playlistId: string, mediaIds: string[], token?: string): Promise<PlaylistItem[] | undefined> => {
    if (!mediaIds?.length) {
      return [];
    }

    const pathname = `/apps/watchlists/${playlistId}`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { token, media_ids: mediaIds });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    if (!data) { throw new Error(`The data was not found using the watchlist ${playlistId}`); }

    return (data.playlist || []).map((item) => this.transformMediaItem(item));
  };

  /**
   * Get media by id
   * @param {string} id
   * @param {string} [token]
   * @param {string} [drmPolicyId]
   */
  getMediaById = async (id: string, token?: string, drmPolicyId?: string): Promise<PlaylistItem | undefined> => {
    const pathname = `/v2/media/${id}`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { token });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;
    const mediaItem = data.playlist[0];

    if (!mediaItem) { throw new Error('MediaItem not found'); }

    return this.transformMediaItem(mediaItem);
  };

  /**
   * Get series by id
   * @param {string} id
   * @param params
   */
  getSeries = async (id: string, params: GetSeriesParams = {}): Promise<Series | undefined> => {
    if (!id) {
      throw new Error('Series ID is required');
    }

    const pathname = `/apps/series/${id}`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, params);
    const response = await fetch(url);

    return (await getDataOrThrow(response)) as Series;
  };

  /**
   * Get all series for the given media_ids
   */
  getSeriesByMediaIds = async (mediaIds: string[]): Promise<Record<string, EpisodeInSeries[]> | undefined> => {
    const pathname = `/apps/series`;
    const url = `${env.APP_API_BASE_URL}${pathname}?media_ids=${mediaIds.join(',')}`;
    const response = await fetch(url);

    return (await getDataOrThrow(response)) as Record<string, EpisodeInSeries[]>;
  };

  /**
   * Get all episodes of the selected series (when no particular season is selected or when episodes are attached to series)
   */
  getEpisodes = async ({
    seriesId,
    pageOffset,
    pageLimit = PAGE_LIMIT,
    afterId,
  }: {
    seriesId: string | undefined;
    pageOffset?: number;
    pageLimit?: number;
    afterId?: string;
  }): Promise<EpisodesWithPagination> => {
    if (!seriesId) {
      throw new Error('Series ID is required');
    }

    const pathname = `/apps/series/${seriesId}/episodes`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, {
      page_offset: pageOffset,
      page_limit: pageLimit,
      after_id: afterId,
    });

    const response = await fetch(url);
    const episodesResponse = (await getDataOrThrow(response)) as EpisodesRes;

    return this.transformEpisodes(episodesResponse);
  };

  /**
   * Get season of the selected series
   */
  getSeasonWithEpisodes = async ({
    seriesId,
    seasonNumber,
    pageOffset,
    pageLimit = PAGE_LIMIT,
  }: {
    seriesId: string | undefined;
    seasonNumber: number;
    pageOffset?: number;
    pageLimit?: number;
  }): Promise<EpisodesWithPagination> => {
    if (!seriesId) {
      throw new Error('Series ID is required');
    }

    const pathname = `/apps/series/${seriesId}/seasons/${seasonNumber}/episodes`;
    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { page_offset: pageOffset, page_limit: pageLimit });

    const response = await fetch(url);
    const episodesRes = (await getDataOrThrow(response)) as EpisodesRes;

    return this.transformEpisodes(episodesRes, seasonNumber);
  };

  getAdSchedule = async (id: string | undefined | null): Promise<AdSchedule | undefined> => {
    if (!id) {
      throw new Error('Ad Schedule ID is required');
    }

    const url = env.APP_API_BASE_URL + `/v2/advertising/schedules/${id}.json`;
    const response = await fetch(url, { credentials: 'omit' });

    return (await getDataOrThrow(response)) as AdSchedule;
  };

  getAppContentSearch = async (siteId: string, searchQuery: string | undefined) => {
    const pathname = `/v2/sites/${siteId}/app_content/media/search`;

    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, {
      search_query: searchQuery,
    });

    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    return this.transformPlaylist(data);
  };

  getDrmMediaById = async (
    id: string,
    siteId: string,
    token: string,
    drmPolicyId: string,
    adConfigId: string,
    adDeliveryMethod: string,
    adEnabled: boolean | string,
  ) => {
    const isSSAI = adDeliveryMethod === 'ssai';
    const pathname = isSSAI && adEnabled ? `/v2/sites/${siteId}/media/${id}/playback.json` : `/v2/media/${id}/drm/${drmPolicyId}`;

    const ssaiUrl = createURL(`${env.APP_API_BASE_URL}${pathname}`, {
      site_id: siteId,
      media_id: id,
      token,
      drm_policy_id: drmPolicyId,
      ad_config_id: adConfigId,
    });

    const csaiUrl = createURL(`${env.APP_API_BASE_URL}${pathname}`, {
      token,
    });

    const url = isSSAI ? ssaiUrl : csaiUrl;

    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    const mediaItem = data.playlist[0];

    return this.transformMediaItem(mediaItem);
  };

  getDrmSignedToken = async (mediaId: string, siteId: string, drmPolicyId: string, adConfigId: string) => {
    if (!adConfigId) { return; }
    const url = `https://jmndgfsre5qgaxkeu7pmem3d6e0irguf.lambda-url.us-east-1.on.aws/`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: `/v2/sites/${siteId}/media/${mediaId}/playback.json`,
        drm_policy_id: drmPolicyId,
        ad_config_id: adConfigId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  getReelsByPlaylistId = async (
    playlistId: string,
    first: number,
    offset: number,
    mediaId?: string,
  ): Promise<Playlist | undefined> => {
    try {
      const { data } = await getAuthApolloClient().query<GetReelsByPlaylistIdData, GetReelsByPlaylistIdVariables>({
        query: GET_REELS_BY_PLAYLIST_ID,
        variables: {
          input: {
            playlistId,
            first,
            offset,
            ...(mediaId ? { mediaId } : {}),
          },
        },
        fetchPolicy: 'network-only',
      });
      if (!data?.getReelsByPlaylistId) { return undefined; }

      const normalizeReel = (reel: any) => {
        let item: any = reel?.JwMediaInfo;

        try {
          item = typeof item === 'string' ? JSON.parse(item) : item;

          const video = item?.playlist?.[0]?.playlist?.[0] || item?.playlist?.[0] || item;

          return {
            ...video,
            mediaid: video?.mediaid || reel.ma_id,
            creatorName: reel.creatorName,
            creatorAvatarUrl: reel.creatorAvatarUrl,
            ma_id: reel.ma_id,
            likeCount: reel.likeCount,
            isLikedByUser: reel.isLikedByUser,
          };
        } catch (e) {
          return {
            mediaid: reel.ma_id,
            title: 'Unknown Video',
            sources: [],
            creatorName: reel.creatorName,
            creatorAvatarUrl: reel.creatorAvatarUrl,
            ma_id: reel.ma_id,
            likeCount: reel.likeCount,
            isLikedByUser: reel.isLikedByUser,
          };
        }
      };

      const reels = data?.getReelsByPlaylistId?.reels?.map(normalizeReel);
      const playListInfo = data?.getReelsByPlaylistId?.playelistInfo;

      return {
        feedid: playlistId,
        title: playListInfo?.title || '',
        cardImageAspectRatio: playListInfo?.cardImageAspectRatio,
        description: playListInfo?.description,
        shelfImageAspectRatio: playListInfo?.shelfImageAspectRatio,
        railType: playListInfo?.railType,
        contentType: playListInfo?.contentType,
        playlist: reels,
        links: {
          next: data.getReelsByPlaylistId.totalCount > offset + data.getReelsByPlaylistId.reels.length ? 'next' : undefined,
        },
      };
    } catch (error) {
      console.error('Error fetching reels:', error);
      return undefined;
    }
  };
}
